import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DiplomaticMission, MissionType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const MISSION_TYPES: MissionType[] = ['HIGH_COMMISSION', 'EMBASSY', 'PERMANENT_MISSION', 'CONSULATE'];

/**
 * Mission directory (GET/POST /missions, GET /missions/:code, PATCH
 * /missions/:id) — the authoritative reference data every other M42 screen
 * (accreditation, VIP-lounge, consular cases, civil events) selects a
 * mission from.
 */
export function Missions() {
  const { persona } = useAuth();
  const isProtocolOfficer = persona?.roles.includes('PROTOCOL_OFFICER') ?? false;
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [status, setStatus] = useState('');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '100' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m42/missions?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<DiplomaticMission>>(path);

  return (
    <div>
      <PageHeader
        title="Mission Directory"
        subtitle="Overseas missions — the reference directory for accreditation, VIP-lounge, consular cases & civil events."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-52" placeholder="Name, city or country…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button type="submit" className="btn-secondary">
              Search
            </button>
            {isProtocolOfficer ? (
              <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
                + New mission
              </button>
            ) : null}
          </form>
        }
      />

      {!isProtocolOfficer ? <RoleNotice roles={['PROTOCOL_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No missions match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">City / country</th>
                <th className="table-head">Type</th>
                <th className="table-head">Head of mission</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((m) => (
                <tr key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedCode(m.code)}>
                  <td className="table-cell font-mono text-xs font-semibold">{m.code}</td>
                  <td className="table-cell font-medium">{m.name}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {m.city}, {m.country}
                  </td>
                  <td className="table-cell text-xs">{m.missionType.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{m.headOfMissionName ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={m.active ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedCode ? (
        <MissionDetailModal
          code={selectedCode}
          canEdit={isProtocolOfficer}
          onClose={() => setSelectedCode(null)}
          onChanged={() => {
            setSelectedCode(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateMissionModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function MissionDetailModal({
  code,
  canEdit,
  onClose,
  onChanged,
}: {
  code: string;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const missionQ = useApiQuery<DiplomaticMission>(`/api/v1/m42/missions/${encodeURIComponent(code)}`);
  const m = missionQ.data;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [head, setHead] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    if (!m) return;
    setName(m.name);
    setHead(m.headOfMissionName ?? '');
    setActive(m.active);
    setEditing(true);
  };

  const save = async () => {
    if (!m) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m42/missions/${m.id}`, { name, headOfMissionName: head || null, active });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to update mission');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={m ? `${m.name} (${m.code})` : 'Mission'} onClose={onClose} wide>
      {missionQ.error ? <ErrorNote message={missionQ.error} /> : null}
      {missionQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {m ? (
        editing ? (
          <div className="space-y-3">
            <Field label="Mission name">
              <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Head of mission">
              <input className="field-input" value={head} onChange={(e) => setHead(e.target.value)} placeholder="Vacant" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </button>
              <button className="btn-primary" onClick={save} disabled={busy || name.trim().length < 3}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
              <Detail label="Status" value={<StatusBadge status={m.active ? 'ACTIVE' : 'CLOSED'} />} />
              <Detail label="Type" value={m.missionType.replaceAll('_', ' ')} />
              <Detail label="City" value={m.city} />
              <Detail label="Country" value={m.country} />
              <Detail label="Head of mission" value={m.headOfMissionName ?? 'Vacant'} />
              <Detail label="Mission code" value={m.code} mono />
            </div>
            {canEdit ? (
              <div className="flex justify-end border-t border-slate-100 pt-4">
                <button className="btn-primary" onClick={startEdit}>
                  Edit mission
                </button>
              </div>
            ) : null}
            <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
              Registered {formatDateTime(m.createdAt)} · last updated {formatDateTime(m.updatedAt)}
            </div>
          </div>
        )
      ) : null}
    </Modal>
  );
}

function CreateMissionModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [missionType, setMissionType] = useState<MissionType>('EMBASSY');
  const [head, setHead] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m42/missions', {
        code: code.toUpperCase(),
        name,
        city,
        country,
        missionType,
        headOfMissionName: head || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create mission');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New overseas mission" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mission code (UPPER_SNAKE_CASE)">
            <input className="field-input font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. DUBAI" />
          </Field>
          <Field label="Type">
            <select className="field-input" value={missionType} onChange={(e) => setMissionType(e.target.value as MissionType)}>
              {MISSION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Mission name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Embassy of Seychelles" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City">
            <input className="field-input" value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label="Country">
            <input className="field-input" value={country} onChange={(e) => setCountry(e.target.value)} />
          </Field>
        </div>
        <Field label="Head of mission (optional)">
          <input className="field-input" value={head} onChange={(e) => setHead(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || code.trim().length < 2 || name.trim().length < 3 || city.trim().length < 2 || country.trim().length < 2}
          >
            {busy ? 'Creating…' : 'Create mission'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
